import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { useAuth } from '../contexts/AuthContext';
import NotificationButton from './NotificationButton';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const linkSx = {
    color: 'text.primary',
    fontSize: '0.75rem',
    letterSpacing: '0.14em',
    fontWeight: 600,
    px: 1.5,
    py: 0.75,
    minWidth: 'auto',
    '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
  } as const;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: alpha(theme.palette.background.default, 0.94),
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: { xs: 60, sm: 72 },
            py: { xs: 0.5, sm: 0 },
            gap: 2,
          }}
        >
          <Box
            component="div"
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            <img
              src="/images/logo.png"
              alt="Allure"
              style={{
                height: isMobile ? 44 : 52,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <Typography
                variant="overline"
                sx={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  letterSpacing: '0.35em',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                ALLURE
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontStyle: 'italic',
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                J'adore Ta Robe
              </Typography>
            </Box>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="text"
                startIcon={<SearchOutlinedIcon sx={{ fontSize: 17 }} />}
                onClick={() => navigate('/search')}
                sx={{
                  ...linkSx,
                  color: location.pathname.startsWith('/search') ? 'primary.main' : 'text.primary',
                }}
              >
                Catalogue
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/search')}
                sx={{
                  letterSpacing: '0.12em',
                  fontSize: '0.65rem',
                  px: 2,
                  py: 0.75,
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  color: theme.palette.primary.main,
                  bgcolor: '#FAF8F5',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    color: 'primary.dark',
                    bgcolor: '#fff',
                  },
                }}
              >
                Louer une robe
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate('/add-dress')}
                sx={{
                  letterSpacing: '0.1em',
                  fontSize: '0.65rem',
                  px: 2,
                  py: 0.75,
                  borderWidth: 2,
                  backgroundColor: alpha('#ffffff', 0.95),
                  backdropFilter: 'blur(8px)',
                  borderColor: alpha(theme.palette.primary.main, 0.55),
                  color: theme.palette.primary.main,
                  WebkitTextFillColor: theme.palette.primary.main,
                  backgroundImage: 'none',
                  '& .MuiButton-startIcon': { color: 'currentColor' },
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                    backgroundImage: 'none',
                    borderColor: theme.palette.primary.main,
                    color: '#ffffff',
                    WebkitTextFillColor: '#ffffff',
                    '& .MuiButton-startIcon': { color: '#ffffff' },
                  },
                }}
              >
                Déposer ma robe
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {isMobile && (
              <IconButton
                aria-label="Rechercher"
                onClick={() => navigate('/search')}
                sx={{ color: 'text.primary' }}
              >
                <SearchOutlinedIcon />
              </IconButton>
            )}
            {user && <NotificationButton />}
            {user ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AccountCircleOutlinedIcon />}
                onClick={() => navigate('/dashboard')}
                sx={{
                  borderColor: 'text.primary',
                  color: 'text.primary',
                  px: { xs: 1, sm: 2 },
                  letterSpacing: '0.1em',
                  fontSize: '0.7rem',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: 'rgba(92, 36, 52, 0.04)',
                  },
                }}
              >
                Espace
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate('/login')}
                sx={{
                  px: { xs: 1.5, sm: 2.5 },
                  fontSize: '0.7rem',
                }}
              >
                Connexion
              </Button>
            )}
          </Box>
        </Toolbar>

        {isMobile && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              pb: 1.5,
              pt: 0.25,
              px: 0,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => navigate('/search')}
              sx={{
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                py: 1,
                borderColor: alpha(theme.palette.primary.main, 0.5),
                color: theme.palette.primary.main,
                bgcolor: '#FAF8F5',
                '&:hover': { bgcolor: '#fff', borderColor: 'primary.dark', color: 'primary.dark' },
              }}
            >
              Louer une robe
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              fullWidth
              onClick={() => navigate('/add-dress')}
              sx={{
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                py: 1,
                borderWidth: 2,
                backgroundColor: alpha('#ffffff', 0.96),
                borderColor: alpha(theme.palette.primary.main, 0.55),
                color: theme.palette.primary.main,
                WebkitTextFillColor: theme.palette.primary.main,
                backgroundImage: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  backgroundImage: 'none',
                  borderColor: theme.palette.primary.main,
                  color: '#ffffff',
                  WebkitTextFillColor: '#ffffff',
                },
              }}
            >
              Déposer ma robe
            </Button>
          </Box>
        )}
      </Container>
    </AppBar>
  );
};

export default Navbar;
